import { lazy, Suspense, type ComponentType } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { HubSpotTrack } from '@/components/HubSpotTrack.tsx';
import Index from "./pages/Index";

// Lazy import with recovery: after a redeploy, old chunk hashes 404.
// Retry once, then force a hard reload to fetch the new build manifest.
const lazyWithReload = <T extends { default: ComponentType<any> }>(
  factory: () => Promise<T>
) =>
  lazy(() =>
    factory().catch(async (error) => {
      try {
        return await factory();
      } catch {
        const key = "chunk-reload";
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          window.location.reload();
          return new Promise<T>(() => {});
        }
        throw error;
      }
    })
  );

const ContactUs = lazyWithReload(() => import("./pages/ContactUs"));
const CaseStudies = lazyWithReload(() => import("./pages/CaseStudies"));
const CaseStudyDetail = lazyWithReload(() => import("./pages/CaseStudyDetail"));
const Solutions = lazyWithReload(() => import("./pages/Solutions"));
const CustomSolutions = lazyWithReload(() => import("./pages/CustomSolutions"));
const GrandPadInAction = lazyWithReload(() => import("./pages/GrandPadInAction"));
const TurnkeySolution = lazyWithReload(() => import("./pages/TurnkeySolution"));
const CombatLoneliness = lazyWithReload(() => import("./pages/CombatLoneliness"));
const SeniorEmpowerment = lazyWithReload(() => import("./pages/SeniorEmpowerment"));
const StyleGuide = lazyWithReload(() => import("./pages/StyleGuide"));
const Downloads = lazyWithReload(() => import("./pages/Downloads"));
const SolutionGuideDetail = lazyWithReload(() => import("./pages/SolutionGuideDetail"));
const NotFound = lazyWithReload(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
	    <HubSpotTrack/>
        <ScrollToTop />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/solutions" element={<CustomSolutions />} />
            <Route path="/grandpad-in-action" element={<GrandPadInAction />} />
            <Route path="/simple-secure-engagement" element={<Solutions />} />
            <Route path="/turnkey-enablement" element={<TurnkeySolution />} />
            <Route path="/evidence-based-outcomes" element={<CombatLoneliness />} />
            <Route path="/person-centered-support" element={<SeniorEmpowerment />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
            <Route path="/style-guide" element={<StyleGuide />} />
            <Route path="/downloads" element={<Downloads />} />
            <Route path="/downloads/:slug" element={<SolutionGuideDetail />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
